<template>
	<div class="hris-page">
	<div class="hris-page-inner">
		<div class="hris-page-header">
			<span class="hris-page-eyebrow">Employee Management</span>
			<h1 class="hris-page-title">Create Company Admin User</h1>
			<p class="hris-page-subtitle">Set up a new administrator account and assign it to a company.</p>
		</div>

		<q-form ref="vForm" @submit="onSubmit">
			<div class="hris-form-section">
				<div class="hris-form-section-title">Personal Information</div>
				<div class="row q-col-gutter-md">
					<div class="col-12 col-md-6 q-gutter-md">
						<q-input outlined dense color="green" v-model="personalInfo.employee_no" label="Employee No." readonly></q-input>
						<q-input outlined dense color="green" v-model="personalInfo.lastname" label="Last Name" :rules="[formRules.required]"></q-input>
						<q-input outlined dense color="green" v-model="personalInfo.firstname" label="First Name" :rules="[formRules.required]"></q-input>
						<q-input outlined dense color="green" v-model="personalInfo.middlename" label="Middle Name"></q-input>
						<q-input outlined dense color="green" v-model="personalInfo.email" label="Email Address"></q-input>
					</div>

					<div class="col-12 col-md-6 q-gutter-md">
						<div class="hris-form-section-title" style="margin-bottom: 0;">Company Assignment</div>
						<q-select v-if="canChooseTenant" outlined dense color="green" v-model="selectedTenantId" option-value="id"
							option-label="name" :options="tenantOptions" label="Tenant" emit-value map-options
							@input="syncCompanyFromTenant"></q-select>
						<q-select outlined dense color="green" v-model="clientInfo.company" option-value="id" option-label="name"
							:options="companyOptions" label="Company" emit-value map-options
							@input="handleCompanySelection"></q-select>
						<q-select outlined dense color="green" v-model="clientInfo.roleID" option-value="id" option-label="name"
							:options="roleOptions" label="Role" emit-value map-options></q-select>
					</div>
				</div>

				<div class="hris-form-actions">
					<q-btn unelevated class="hris-q-primary" style="min-width: 150px;" type="submit" label="Submit"></q-btn>
				</div>
			</div>
		</q-form>
	</div>

	<q-dialog v-model="spinner" persistent>
		<div class="hris-loading" style="background: var(--hris-surface); border-radius: var(--hris-radius);">
			<q-spinner color="green" size="40px"></q-spinner>
			<span>Please wait…</span>
		</div>
	</q-dialog>
	</div>
</template>

<script>
import axios from 'axios';

export default {
	name: 'CreateCompanyUser',
	data() {
		return {
			spinner: false,
			allCompanyOptions: [],
			companyOptions: [],
			tenantOptions: [],
			roleOptions: [],
			selectedTenantId: null,
			companyLookup: {},
			companyTenantCache: {},
			personalInfo: {
				employee_no: null,
				firstname: null,
				middlename: null,
				lastname: null,
				email: null,
			},
			clientInfo: {
				company: null,
				roleID: null,
			},
		};
	},
	created() {
		this.getDropdownProperties();
	},
	computed: {
		canChooseTenant() {
			const user = this.userData || {};
			const roleId = Number(user.role_id ?? user.role ?? -1);
			const isSaasOwner = Boolean(user.is_saas_owner ?? user.is_sass_owner);
			return roleId === 0 || isSaasOwner;
		},
		userTenantId() {
			const user = this.userData || {};
			return Number(user.tenant_id ?? user.tenant ?? 0) || null;
		},
	},
	methods: {
		normalizeNullable(value) {
			if (value === null || value === undefined) {
				return null;
			}
			if (typeof value === 'string') {
				const trimmed = value.trim();
				if (trimmed === '' || trimmed.toLowerCase() === 'null' || trimmed.toLowerCase() === 'undefined') {
					return null;
				}
				return trimmed;
			}
			return value;
		},

		appendFormValue(formData, key, value, defaultValue = '') {
			const normalized = this.normalizeNullable(value);
			if (Array.isArray(normalized)) {
				formData.append(key, normalized.join(','));
				return;
			}
			formData.append(key, normalized === null ? defaultValue : normalized);
		},

		onSubmit(event) {
			this.spinner = true;
			const tenantPayloadId = this.resolveTenantPayloadId();

			if (!tenantPayloadId) {
				this.spinner = false;
				this.$q.notify({
					message: 'Please select a tenant for this company user.',
					color: 'negative',
					position: 'center',
					actions: [{ label: 'Dismiss', color: 'white' }],
				});
				return;
			}

			const formData = new FormData();
			this.appendFormValue(formData, 'employee_id', this.personalInfo.employee_no);
			this.appendFormValue(formData, 'firstname', this.personalInfo.firstname);
			this.appendFormValue(formData, 'middlename', this.personalInfo.middlename);
			this.appendFormValue(formData, 'lastname', this.personalInfo.lastname);
			this.appendFormValue(formData, 'email', this.personalInfo.email);
			this.appendFormValue(formData, 'company', this.clientInfo.company);
			this.appendFormValue(formData, 'tenant_id', tenantPayloadId);
			this.appendFormValue(formData, 'role_id', this.clientInfo.roleID);
			const selectedRole = this.roleOptions.find((role) => role.id === this.clientInfo.roleID);
			this.appendFormValue(formData, 'role', selectedRole ? selectedRole.name : null);
			this.appendFormValue(formData, 'client_reference', 'new_client');
			axios.create({
				baseURL: this.apiUrl,
				headers: { Authorization: `Bearer ${this.token}` }
			})
				.post('/employee/create/company-user', formData)
				.then((res) => {
					this.spinner = false;
					this.resetForm();
					const password = res.data && res.data.data ? res.data.data.password : null;
					const baseMessage = 'New company user created';
					this.$q.notify({
						message: password ? `${baseMessage} (password: ${password})` : baseMessage,
						color: 'green',
						position: 'center',
						actions: [{ label: 'Dismiss', color: 'white' }],
					});
				})
				.catch((error) => {
					this.spinner = false;
					const serverMessage = error && error.response && error.response.data ? error.response.data.message : null;
					const message = serverMessage || 'Unable to create employee. Please check the form fields and try again.';
					this.$q.notify({
						message,
						color: 'negative',
						position: 'center',
						actions: [{ label: 'Dismiss', color: 'white' }],
					});
				});
		},

		resetForm() {
			this.personalInfo = {
				employee_no: null,
				firstname: null,
				middlename: null,
				lastname: null,
				email: null,
			};
			this.clientInfo = {
				company: null,
				roleID: null,
			};
			this.setDefaultTenantSelection();
			this.applyCompanyFilters();
		},

		validateInput(value) {
			if (value == null || value === '' || value === 'NULL' || value === 'null' || value === 'undefined') {
				return 'red';
			}
			return 'green';
		},

		getDropdownProperties() {
			axios.create({
				baseURL: this.apiUrl,
				headers: { Authorization: `Bearer ${this.token}` }
			})
				.get('/employee/dropdown/properties')
				.then((res) => {
					const data = res.data.data;
					this.allCompanyOptions = data.companies || [];
					this.tenantOptions = data.tenants || [];
					this.roleOptions = data.roles;
					this.setDefaultTenantSelection();
					this.applyCompanyFilters();
				});
		},

		setDefaultTenantSelection() {
			if (this.selectedTenantId) {
				return;
			}

			this.selectedTenantId = this.canChooseTenant
				? (this.tenantOptions[0]?.id ?? this.userTenantId)
				: this.userTenantId;
		},

		applyCompanyFilters() {
			this.prepareCompanyHierarchy();
			if (!this.allCompanyOptions.length) {
				this.companyOptions = [];
				this.clientInfo.company = null;
				return;
			}

			if (this.canChooseTenant && this.selectedTenantId) {
				this.companyOptions = this.allCompanyOptions.filter((company) => {
					return Number(this.resolveTenantFromCompanyId(company.id)) === Number(this.selectedTenantId);
				});
			} else {
				this.companyOptions = [...this.allCompanyOptions];
			}

			const selectedCompany = this.companyOptions.find((company) => {
				return Number(company.id) === Number(this.clientInfo.company);
			});
			if (!selectedCompany) {
				this.clientInfo.company = null;
			}
		},

		prepareCompanyHierarchy() {
			this.companyLookup = {};
			this.companyTenantCache = {};
			this.allCompanyOptions.forEach((company) => {
				const key = Number(company.id);
				if (key) {
					this.companyLookup[key] = company;
				}
			});
		},

		resolveTenantFromCompanyId(companyId, visited = new Set()) {
			const id = Number(companyId);
			if (!id || this.companyTenantCache[id] !== undefined) {
				return this.companyTenantCache[id] ?? null;
			}
			if (visited.has(id)) {
				this.companyTenantCache[id] = null;
				return null;
			}

			const company = this.companyLookup[id];
			if (!company) {
				this.companyTenantCache[id] = null;
				return null;
			}
			if (company.tenant_id) {
				const tenantId = Number(company.tenant_id);
				this.companyTenantCache[id] = tenantId;
				return tenantId;
			}

			visited.add(id);
			const parentTenantId = this.resolveTenantFromCompanyId(company.parent_id, visited);
			this.companyTenantCache[id] = parentTenantId;
			return parentTenantId;
		},

		syncCompanyFromTenant() {
			this.applyCompanyFilters();
		},

		handleCompanySelection(companyId) {
			this.syncTenantFromCompany(companyId);
			this.generateNewEmployeeID(companyId);
		},

		syncTenantFromCompany(companyId) {
			const tenantId = this.resolveTenantFromCompanyId(companyId);
			if (tenantId) {
				this.selectedTenantId = tenantId;
			}
		},

		resolveTenantPayloadId() {
			if (this.clientInfo.company) {
				return this.resolveTenantFromCompanyId(this.clientInfo.company) ?? this.selectedTenantId ?? this.userTenantId;
			}

			return this.selectedTenantId ?? this.userTenantId;
		},

		generateNewEmployeeID(companyId = null) {
			if (!companyId) {
				this.personalInfo.employee_no = null;
				return;
			}

			const formData = new FormData();
			formData.append('id', companyId);
			axios.create({
				baseURL: this.apiUrl,
				headers: { Authorization: `Bearer ${this.token}` }
			})
				.post('/employeeno/generate', formData)
				.then((res) => {
					this.personalInfo.employee_no = res.data.data;
				});
		},
	},
};
</script>

<style scoped></style>
